/* Two-part form: search OpenLibrary for a book, then fill in details and submit. */

import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, ChevronDown, ChevronRight, BookOpen, TrendingUp } from "lucide-react";
import { useBookSearch, type BookSearchResult } from "@/hooks/useBookSearch";
import { BookBrowsePopular } from "@/components/books/BookBrowsePopular";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import type { PopularBook } from "@/types/bookDiscover";

interface AddBookFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

export function AddBookForm({ onSuccess, footerContainer }: AddBookFormProps) {
  const { results, isSearching, error: searchError, search } = useBookSearch();
  const [searchQuery, setSearchQuery] = useState("");

  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [totalPages, setTotalPages] = useState("");
  const [pagesPerDay, setPagesPerDay] = useState("20");
  const [note, setNote] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [topic, setTopic] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [link, setLink] = useState("");
  const [publicationDate, setPublicationDate] = useState("");
  const [startImmediately, setStartImmediately] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showPopular, setShowPopular] = useState(false);

  const createBook = useMutation(api.books.create);
  const startBook = useMutation(api.books.start);

  function handleSelectBook(book: BookSearchResult) {
    setSelectedKey(book.key);
    setTitle(book.title);
    setAuthor(book.author);
    setTotalPages(book.pageCount ? String(book.pageCount) : "");
    setCoverUrl(book.coverUrl ?? "");
    setLink(book.link);
    setPublicationDate(book.year ? String(book.year) : "");
  }

  function handleSelectPopularBook(book: PopularBook) {
    setShowPopular(false);
    setSelectedKey(book.key);
    setTitle(book.title);
    setAuthor(book.author);
    setTotalPages(book.pageCount ? String(book.pageCount) : "");
    setCoverUrl(book.coverUrl ?? "");
    setLink(`https://openlibrary.org${book.key}`);
    setPublicationDate(book.year ? String(book.year) : "");
  }

  function handleSearch() {
    search(searchQuery);
    setShowPopular(false);
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  }

  const canSubmit =
    title.trim().length > 0 &&
    author.trim().length > 0 &&
    Number(totalPages) > 0 &&
    Number(pagesPerDay) > 0 &&
    !isSubmitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const bookId = await createBook({
        title: title.trim(),
        author: author.trim(),
        totalPages: Number(totalPages),
        pagesPerDay: Number(pagesPerDay),
        note: note.trim() || undefined,
        topic: topic.trim() || undefined,
        coverUrl: coverUrl.trim() || undefined,
        link: link.trim() || undefined,
        publicationDate: publicationDate.trim() || undefined,
      });
      if (startImmediately) {
        await startBook({ id: bookId });
      }
      posthog.capture(Events.BOOK_ADDED, { title: title.trim(), startImmediately });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add book";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      {/* Search section */}
      <div className="space-y-2">
        <Label>Search OpenLibrary</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Search by title or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          <Button
            variant="outline"
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
          >
            {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>
        {searchError && <p className="text-destructive text-sm">{searchError}</p>}
      </div>

      {/* Browse Popular toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowPopular(!showPopular)}
        className="w-full"
      >
        <TrendingUp className="size-4" />
        {showPopular ? "Hide Popular Books" : "Browse Popular Books"}
      </Button>

      {/* Popular books section */}
      {showPopular && (
        <BookBrowsePopular onSelectBook={handleSelectPopularBook} />
      )}

      {/* Search results grid */}
      {results.length > 0 && !showPopular && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {results.map((book) => (
            <button
              key={book.key}
              type="button"
              className={`flex flex-col items-center gap-1 p-2 rounded-md border cursor-pointer hover:bg-muted/50 shrink-0 w-[90px] ${selectedKey === book.key ? "border-primary bg-muted/50" : "border-border"}`}
              onClick={() => handleSelectBook(book)}
            >
              {book.coverUrl ? (
                <img
                  src={book.coverUrl}
                  alt={book.title}
                  className="w-12 h-[72px] rounded-sm object-cover bg-muted"
                />
              ) : (
                <div className="w-12 h-[72px] rounded-sm bg-muted flex items-center justify-center">
                  <BookOpen className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {book.title}
              </span>
              <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                {book.author}{book.year ? ` (${book.year})` : ""}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Details section */}
      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="book-title">Title</Label>
          <Input
            id="book-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Book title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="book-author">Author</Label>
          <Input
            id="book-author"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Author name"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="book-pages">Total Pages</Label>
            <Input
              id="book-pages"
              type="number"
              min={1}
              value={totalPages}
              onChange={(e) => setTotalPages(e.target.value)}
              placeholder="e.g. 300"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="book-pace">Pages Per Day</Label>
            <Input
              id="book-pace"
              type="number"
              min={1}
              value={pagesPerDay}
              onChange={(e) => setPagesPerDay(e.target.value)}
              placeholder="e.g. 20"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="book-note">Note (optional)</Label>
          <Textarea
            id="book-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why you want to read this book..."
            rows={2}
          />
        </div>

        {/* Advanced section */}
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          Advanced
        </button>

        {showAdvanced && (
          <div className="space-y-3 pl-2 border-l-2 border-border">
            <div className="space-y-2">
              <Label htmlFor="book-topic">Topic</Label>
              <Input
                id="book-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Self-improvement"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="book-cover">Cover URL</Label>
              <Input
                id="book-cover"
                value={coverUrl}
                onChange={(e) => setCoverUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="book-link">Book Link</Label>
              <Input
                id="book-link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="book-date">Publication Date</Label>
              <Input
                id="book-date"
                value={publicationDate}
                onChange={(e) => setPublicationDate(e.target.value)}
                placeholder="e.g. 2023"
              />
            </div>
          </div>
        )}

        {/* Start immediately checkbox */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={startImmediately}
            onChange={(e) => setStartImmediately(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm">Start reading immediately</span>
        </label>
      </div>

      {submitError && <p className="text-destructive text-sm">{submitError}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add Book
        </Button>,
        footerContainer,
      ) : (
        <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add Book
        </Button>
      )}
    </div>
  );
}
